// ══════════════════════════════════════════════════════════════════════════════
//  Card, KPICard y EmptyState.
// ══════════════════════════════════════════════════════════════════════════════
import { C, T, R, E, F } from '../../design.js';
import { Icon } from './Icon.js';
import { Btn } from './botones.js';

const Card = ({ children, style:sx={}, onClick, hover=false, ...rest }) => {
  const interactive = !!onClick || hover;
  return (
    <div onClick={onClick}
      className={["gb-i", interactive && "gb-lift", onClick && "gb-press gb-focus"].filter(Boolean).join(" ")}
      tabIndex={onClick ? 0 : undefined} role={onClick ? "button" : undefined}
      onKeyDown={onClick ? (e)=>{ if(e.key==="Enter"||e.key===" "){ e.preventDefault(); onClick(e); } } : undefined}
      style={{background:C.surface,borderRadius:R.card,boxShadow:E.e1,...sx}} {...rest}>{children}</div>
  );
};

// ══════════════════════════════════════════════
//  KPI CARD — soporta dos modos:
//  • Legacy: <KPICard icon={<Icon ... />} />        (sin tone/size → look anterior)
//  • Premium (Oleada 2): <KPICard icon="bell" tone="primary" size="md" />
// ══════════════════════════════════════════════
const KPICard = ({ label, value, sub, icon, color=C.primary, tone, size, trend, spark }) => {
  const usePrem = tone || size;
  if (!usePrem) {
    return (
      <Card style={{padding:"14px 16px",minWidth:0,overflow:"hidden"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
          <span style={{...T.caption,color:C.textSub,flex:1,paddingRight:4,wordBreak:"break-word"}}>{label}</span>
          <span style={{flexShrink:0}}>{icon}</span>
        </div>
        <div style={{...T.headingLg,...F.num,color:C.text,letterSpacing:0,marginBottom:3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",minWidth:0,lineHeight:1}}>{value}</div>
        {sub && <div style={{fontSize:10,color:C.textMuted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{sub}</div>}
        {trend && <div style={{fontSize:10,color:trend>0?C.success:C.danger,marginTop:2}}>{trend>0?"▲":"▼"} {Math.abs(trend)}%</div>}
        {spark && <div style={{marginTop:6}}>{spark}</div>}
      </Card>
    );
  }
  const tones = {
    neutral: { iconBg: C.bg,           iconColor: C.textSub },
    primary: { iconBg: C.primaryLight, iconColor: C.primary },
    success: { iconBg: C.successLight, iconColor: C.success },
    warning: { iconBg: C.warningLight, iconColor: C.warning },
    danger:  { iconBg: C.dangerLight,  iconColor: C.danger  },
    purple:  { iconBg: C.purpleLight,  iconColor: C.purple  },
    accent:  { iconBg: C.accentLight,  iconColor: C.accent  },
    mark:    { iconBg: C.markLight,    iconColor: C.markDeep },   // etapa "estimada" del ciclo de cobro (naranja de cobro_badge)
  };
  const t = tones[tone] || tones.neutral;
  const sizes = {
    sm: { padding: "10px 12px", valueFs: 16, iconBox: 22, iconSize: 11 },
    md: { padding: "12px 14px", valueFs: 18, iconBox: 26, iconSize: 12 },
    lg: { padding: "14px 16px", valueFs: 22, iconBox: 32, iconSize: 16 },
  };
  const sz = sizes[size] || sizes.md;
  const iconEl = icon
    ? (typeof icon === "string"
        ? <Icon name={icon} size={sz.iconSize} color={t.iconColor}/>
        : icon)
    : null;
  return (
    <div style={{background:C.surface, borderRadius:R.card, padding:sz.padding, boxShadow:E.e1, minWidth:0, overflow:"hidden"}}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6}}>
        <span style={{...T.caption, color:C.textMuted, flex:1, paddingRight:4, wordBreak:"break-word"}}>{label}</span>
        {iconEl && (
          <div style={{width:sz.iconBox, height:sz.iconBox, borderRadius:R.sm, background:t.iconBg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}>
            {iconEl}
          </div>
        )}
      </div>
      <div style={{...F.num, fontFamily:F.display, fontSize:sz.valueFs, fontWeight:700, color:C.text, marginBottom:3, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:0, lineHeight:1.1}}>{value}</div>
      {sub && <div style={{fontSize:10, color:C.textMuted, fontWeight:500, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>{sub}</div>}
      {spark && <div style={{marginTop:8}}>{spark}</div>}
    </div>
  );
};

// ── Estado vacío reutilizable ──────────────────
//  `icon` acepta nombre (string → Icon por nombre) o ReactNode (retrocompat).
//  `tone` tiñe icono+título (success/danger/warning).
const EmptyState = ({ icon, title, description, action, onAction, tone }) => {
  const toneColor = tone==="success"?C.success : tone==="danger"?C.critical : tone==="warning"?C.warning : undefined;
  const iconEl = typeof icon==="string" ? <Icon name={icon} size={40} color={toneColor||C.borderMid}/> : icon;
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"60px 24px",textAlign:"center",gap:12}}>
      <div style={{marginBottom:4,display:"flex",alignItems:"center",justifyContent:"center",color:toneColor}}>{iconEl}</div>
      <div style={{...T.headingSm,color:toneColor||C.text}}>{title}</div>
      <div style={{...T.body,color:C.textMuted,maxWidth:360,lineHeight:1.6}}>{description}</div>
      {action && onAction && (
        <Btn onClick={onAction} style={{marginTop:8}}>{action}</Btn>
      )}
    </div>
  );
};


export { Card, EmptyState, KPICard };
